import React, { useState } from 'react'
import styled, { css } from 'styled-components'
import { Spacings, Text } from '@styles'

const Wrapper = styled.div`
	display: inline-flex;
	align-items: center;
	gap: ${Spacings.SPACING_2B};
	font-family: ${({ theme }) => theme.fontFamily?.inter || "'Inter', sans-serif"};
`

const Label = styled.span`
	font-size: ${Text.SMALL};
	font-weight: ${Text.BOLD_500};
	color: ${({ theme }) => theme.colors.text.secondary};
`

// Figma has no exact token for this 5px radius (closest tokens are 4px/6px) — kept literal
// on the segmented variant so it matches the design pixel-for-pixel.
const SEGMENTED_RADIUS = '5px'

const Track = styled.div`
	display: inline-flex;
	align-items: center;
	border: 1px solid ${({ theme }) => theme.colors.brand.muted};

	${({ $variant, theme }) =>
		$variant === 'segmented'
			? css`
					gap: 0;
					padding: ${Spacings.SPACING_5};
					background: ${theme.colors.brand.subtle};
					border-radius: ${SEGMENTED_RADIUS};
			  `
			: css`
					gap: ${Spacings.SPACING_1};
					padding: ${Spacings.SPACING_1};
					background: ${theme.colors.surface.card};
					border-radius: ${theme.radius.full};
			  `}

	${({ disabled }) =>
		disabled &&
		css`
			opacity: 0.6;
			pointer-events: none;
		`}
`

const segmentedRadius = ({ $isFirst, $isLast, $isOnly }) => {
	if ($isOnly) return SEGMENTED_RADIUS
	if ($isFirst) return `${SEGMENTED_RADIUS} 0 0 ${SEGMENTED_RADIUS}`
	if ($isLast) return `0 ${SEGMENTED_RADIUS} ${SEGMENTED_RADIUS} 0`
	return '0'
}

const Option = styled.button`
	font-family: inherit;
	cursor: ${({ $active }) => ($active ? 'default' : 'pointer')};
	user-select: none;
	transition: all 0.15s ease-in-out;
	color: ${({ theme, $active }) => ($active ? theme.colors.text.inverse : theme.colors.text.primary)};

	${({ $variant, $active, theme }) =>
		$variant === 'segmented'
			? css`
					background: ${$active ? theme.colors.brand.primary : theme.colors.surface.card};
					border: none;
					padding: ${Spacings.SPACING_5} ${Spacings.SPACING_15};
					border-radius: ${segmentedRadius};
					font-size: ${Text.SMALL};
					font-weight: ${Text.BOLD_500};
					line-height: 1rem;
			  `
			: css`
					background: ${$active ? theme.colors.brand.primary : 'transparent'};
					border: 1px solid transparent;
					min-width: ${Spacings.SPACING_14B};
					padding: ${Spacings.SPACING_1B} ${Spacings.SPACING_4B};
					border-radius: ${theme.radius.full};
					font-size: ${Text.MEDIUM};
					font-weight: ${$active ? Text.BOLD_700 : Text.BOLD_500};
			  `}
`

// Two ways to drive the toggle — any number of options works with either (2, 3 or more):
//
// 1. Legacy shape: options: [{ label, active, click }] — the caller owns the active flag (same as the v1 base Toggle).
// 2. Value shape:  options: [{ label, value }] plus `onChange`. Give `value` to control it from outside, or
//    `defaultValue` to let the toggle hold its own selection. `defaultValue` is the developer's pick of which
//    option starts selected — typically a neutral one such as { label: 'All', value: null }.
//
// label: optional text rendered before the toggle.
// variant: 'segmented' (default, full-rounded capsule) or 'pill' (flat, touching segments — Figma node 12598:108783).
const Toggle = ({ options = [], disabled, label, className, value, defaultValue, onChange, variant = 'segmented' }) => {
	// An option carrying a `value` key opts into the value API; otherwise fall back to the `active` flags.
	const usesValueApi = options.some((option) => 'value' in option)
	const isControlled = value !== undefined

	const [internalValue, setInternalValue] = useState(() => {
		if (defaultValue !== undefined) return defaultValue
		return options.find((option) => option.active)?.value
	})

	const selectedValue = isControlled ? value : internalValue

	const isActive = (option) => (usesValueApi ? option.value === selectedValue : !!option.active)

	const clickHandler = (option) => {
		if (usesValueApi) {
			if (!isControlled) setInternalValue(option.value)
			onChange?.(option.value, option)
		}
		option.click?.()
	}

	return (
		<Wrapper className={className}>
			{label && <Label>{label}</Label>}
			<Track disabled={disabled} $variant={variant}>
				{options.map((option, index) => (
					<Option
						key={`toggle-option-${index}`}
						type='button'
						$variant={variant}
						$active={isActive(option)}
						$isFirst={index === 0}
						$isLast={index === options.length - 1}
						$isOnly={options.length === 1}
						onClick={disabled ? undefined : () => clickHandler(option)}
					>
						{option.label}
					</Option>
				))}
			</Track>
		</Wrapper>
	)
}

export default Toggle
